/**
 * Clinic-wide account preferences and experimental feature flags (EN-1075).
 *
 * Replaces the direct-Supabase writers in supabaseClient.ts (`updateAccountMetadata`,
 * `updateCopyForMarkdown`). `updateCopyForMarkdown` keyed the write off `session.user.id`, which
 * was only correct while `account.id == auth.uid()` held: post-EN-426 it matched zero rows for a
 * non-owner member, and while impersonating it read the target's metadata but wrote it onto the
 * admin's own row. `updateAccountMetadata` took the clinic account id, so it was already
 * member-correct - it is replaced for the chokepoint, not because it was keyed wrong.
 *
 * The endpoints take no account id - the server resolves the clinic from the session, and
 * `apiFetch` supplies `X-Effective-User-Id` / `X-HappyDoc-Account-Id` - so there is no user id
 * in this path left to get wrong.
 */

import { apiFetch } from "@common/lib/api";
import type { AccountMetaData, FeatureFlags } from "@common/utils/types";

/** Only the keys the API accepts; a partial patch, so send just what changed. */
export type AccountPreferencesPatch = Partial<
    Pick<
        AccountMetaData,
        "default_view" | "markdown_copy_enabled" | "finalize_confetti_disabled" | "floating_buttons_hidden"
    >
>;

/**
 * The shape both endpoints below answer with. `apiFetch` returns `response.json()` whole and does
 * not unwrap `.data`, so the payload has to be read through the envelope - reading the key off the
 * root found undefined and threw on a write that had already committed, which made every control
 * on Settings > Organization revert and report "not saved" while the value was in fact saved.
 */
type ApiEnvelope<T> = { status?: string; data?: T };

/**
 * Merges preference keys into the clinic's `meta_data` and returns the merged blob.
 * Throws on failure (apiFetch has already reported to Sentry), so callers can revert optimistic UI.
 */
export async function patchAccountPreferences(patch: AccountPreferencesPatch): Promise<AccountMetaData> {
    const response = await apiFetch<ApiEnvelope<{ meta_data: AccountMetaData }>>("account/preferences", {
        method: "PATCH",
        json: patch,
    });
    const metaData = response?.data?.meta_data;
    if (!metaData) throw new Error("Account preferences: empty response");
    return metaData;
}

/**
 * Toggles one experimental feature flag. Owner-only server-side (403 otherwise), and the API
 * allow-lists which flag names may be set from the app at all.
 */
export async function patchAccountFeatureFlag(flag: FeatureFlags, enabled: boolean): Promise<string[]> {
    const response = await apiFetch<ApiEnvelope<{ feature_flags: string[] }>>("account/feature-flags", {
        method: "PATCH",
        json: { flag, enabled },
    });
    const flags = response?.data?.feature_flags;
    // Turning off the last enabled flag legitimately returns [], so test for the key, not truthiness.
    if (!flags) throw new Error("Account feature flags: empty response");
    return flags;
}
